/**
 * filterOperators.ts
 *
 * The single typed + localized operator derivation for property-based filter
 * editors. Every property-filter UI (PropertyFilterMolecule, FilterEditorPanelMolecule,
 * AdvancedFilterEditorOrganism, and the Phase 8 column-filter migrations) must
 * resolve its operator list through this function instead of hand-rolling one —
 * prior to this extraction the same switch existed five different ways and
 * disagreed (some hardcoded English, some type-blind, one offering `greater_than`
 * on booleans). See spec context-selector-editor.md Phase 1 (SC8/SC9).
 */

import type { FilterExpression, FilterGroup, FilterGroupLeaf, FilterOperator } from 'types/filters';
import { isPerspectiveLeaf, isPropertyLeaf, isRelationshipLeaf } from '@center-platform/spheres/filters';
import type { PropertyOption, PropertyType } from '../components/molecules/PropertyFilter/PropertyFilterMolecule';

export interface FilterOperatorOption {
  label: string;
  value: FilterOperator;
}

// `count` is i18next's plural selector — summaries that render a quantity
// (e.g. "2 assignees") must pluralize through t(), never concatenate a bare
// integer, so the alias has to admit it. Other keys are interpolation values
// (e.g. a range summary's `{{min}}`/`{{max}}`), for the same reason.
export type TranslateFn = (
  key: string,
  options?: { defaultValue?: string; count?: number } & Record<string, string | number | undefined>
) => string;

/**
 * Operators that don't require a value input (is_empty / is_not_empty / is_true /
 * is_false / last_7_days / last_30_days). Shared so every consumer agrees on which
 * rows skip the value editor.
 */
export const VALUELESS_OPERATORS: readonly FilterOperator[] = [
  'is_empty',
  'is_not_empty',
  'is_true',
  'is_false',
  'last_7_days',
  'last_30_days',
  'exists',
  'not_exists',
];

/**
 * Operators whose value is a single relative day-count (`within_last_days` /
 * `within_next_days` — filter-row.jsx's "in the last" / "in the next", canonicalized
 * to a plain integer day count rather than the mock's `{n, unit}` pair: the Gremlin/ES
 * evaluators (`gremlinFilterBuilder.ts`, `buildElasticsearchQuery.ts`) only ever read
 * `expression.value` as a day count, so "weeks"/"months" would be a UI concept the
 * engine can't express — days is the canonical, and only, granularity).
 *
 * `date_within_days` belongs to this set for the same reason — its operand is a day
 * count read from `expression.value` — even though its WINDOW differs: it is
 * SYMMETRIC (`Math.abs` of the day difference — past *or* future, see `COMPARATORS`
 * in `packages/utils/spheres/src/lib/operators.ts` and `ES_CLAUSE_BUILDERS`'s
 * `now-Ns`/`now+Ns` range), while `within_last_days`/`within_next_days` are strictly
 * directional. Membership here decides which VALUE EDITOR a row renders (a day-count
 * number input), never which window the engine computes; its label carries the
 * direction (`filters.operators.dateWithinDays` names "past or future" outright) so a
 * reader can never mistake it for the directional pair.
 */
export const RELATIVE_DAYS_OPERATORS: readonly FilterOperator[] = [
  'within_last_days',
  'within_next_days',
  'date_within_days',
];

/**
 * Operators whose operand is the `values` ARRAY rather than the scalar `value` —
 * the UI-side mirror of `expectedOperand` (`@center-platform/spheres/filters`).
 * Crossing this boundary when the operator changes must move the operand too, or
 * the row keeps a field the new operator does not read and silently matches
 * nothing (`in` with `value: 'x'` hands the comparator `undefined`).
 */
export const ARRAY_OPERAND_OPERATORS: readonly FilterOperator[] = ['in', 'not_in', 'contains_all'];

function isFilledValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/**
 * One `between` bound as the canonical number every engine reads — a number
 * passes through; a numeric string becomes that number; a date (a `Date`, or
 * an ISO string a date/datetime editor wrote) becomes epoch milliseconds, the
 * convention `ColumnFilterHeaderAtom`'s wire format already uses for date
 * ranges. Anything else — empty, `@today`, unparseable — has no canonical
 * numeric form and reads as an unset bound.
 */
export function toBetweenBound(raw: unknown): number | undefined {
  if (isFiniteNumber(raw)) return raw;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? undefined : raw.getTime();
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return asNumber;
  const asDate = Date.parse(raw);
  return Number.isNaN(asDate) ? undefined : asDate;
}

/**
 * One `between` bound for a date/datetime property, as the canonical ISO
 * 8601 UTC string every engine now compares lexicographically (Phase 7a —
 * one date representation, EAINT-931): `compareValues`, `gremlinFilterBuilder`
 * and `buildElasticsearchQuery` all compare date `between` bounds as
 * `Z`-suffixed, fixed-millisecond-precision strings. Accepts a `Date` (what
 * `CalendarAtom`'s `onChange` hands back), an ISO string (what
 * `DateTimePickerInputAtom`'s `onIsoChange` hands back), or — read-back
 * compatibility only — a legacy epoch-ms number/numeric string a pre-7a
 * editor already persisted; nothing writes ms anymore. Anything else —
 * empty, `@today`, unparseable — has no canonical form.
 */
export function toISODateBound(raw: unknown): string | undefined {
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? undefined : raw.toISOString();
  if (isFiniteNumber(raw)) return new Date(raw).toISOString();
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return new Date(asNumber).toISOString();
  const asDate = Date.parse(raw);
  return Number.isNaN(asDate) ? undefined : new Date(asDate).toISOString();
}

/**
 * One bound of a legacy `value: [from, to]` pair in its canonical form: a
 * number or numeric string stays numeric (a numeric property), a date string
 * becomes the ISO 8601 UTC string the engines compare date bounds as. Legacy
 * pairs never carried epoch ms for a date, so a date is never turned into one.
 */
function toLegacyBetweenBound(raw: unknown): number | string | undefined {
  if (isFiniteNumber(raw)) return raw;
  if (typeof raw !== 'string' || raw.trim() === '') return undefined;
  const asNumber = Number(raw);
  if (Number.isFinite(asNumber)) return asNumber;
  return toISODateBound(raw);
}

/** A `between` bound with a canonical form — a finite number, or a non-empty string (an ISO date or `@today`). */
export const isUsableBetweenBound = (value: unknown): value is number | string =>
  isFiniteNumber(value) || (typeof value === 'string' && value.trim() !== '');

/**
 * A `between` row's bounds, read from the canonical `min`/`max` fields
 * (`types/filters.d.ts`, `/standards/shared/filtering.md` — the only fields
 * the Gremlin, Elasticsearch and in-memory engines read) or, for a row stored
 * before the editor wrote the canonical shape, from the legacy `value: [from,
 * to]` pair. Canonical fields win when both are present. A bound is a number
 * for a numeric property, or an ISO string (or, read-back only, a legacy
 * epoch-ms number already persisted) for a date/datetime property.
 */
export function readBetweenBounds(filter: FilterExpression): { min?: number | string; max?: number | string } {
  if (isUsableBetweenBound(filter.min) || isUsableBetweenBound(filter.max)) {
    return {
      min: isUsableBetweenBound(filter.min) ? filter.min : undefined,
      max: isUsableBetweenBound(filter.max) ? filter.max : undefined,
    };
  }
  if (Array.isArray(filter.value)) {
    return { min: toLegacyBetweenBound(filter.value[0]), max: toLegacyBetweenBound(filter.value[1]) };
  }
  return {};
}

/**
 * Rewrites a `between` row still in the legacy `value: [from, to]` shape into
 * the canonical `min`/`max` shape, clearing `value`/`values`. Every other row
 * — including a `between` already canonical — is returned as the same object,
 * so callers can detect "nothing to normalise" by identity.
 */
export function normalizeBetweenExpression(filter: FilterExpression): FilterExpression {
  if (filter.operator !== 'between' || !Array.isArray(filter.value)) return filter;
  const { min, max } = readBetweenBounds(filter);
  return { ...filter, min, max, value: undefined, values: undefined };
}

/**
 * The same normalisation applied to every property leaf of a group tree, at
 * every depth. Returns the input group unchanged (same reference) when no leaf
 * needed rewriting, so a memoised caller does not churn on an already
 * canonical tree.
 */
export function normalizeBetweenInGroup(group: FilterGroup): FilterGroup {
  let changed = false;
  const expressions = (group.expressions ?? []).map((leaf): FilterGroupLeaf => {
    if (!isPropertyLeaf(leaf)) return leaf;
    const next = normalizeBetweenExpression(leaf);
    if (next !== leaf) changed = true;
    return next;
  });
  const groups = group.groups?.map((child) => {
    const next = normalizeBetweenInGroup(child);
    if (next !== child) changed = true;
    return next;
  });
  return changed ? { ...group, expressions, groups } : group;
}

/**
 * Whether a row carries everything its operator needs to evaluate, judged on
 * the row alone — no property catalog. A row with no property, a
 * value-requiring operator with nothing entered, or a `between` missing either
 * canonical bound is incomplete. `between` requires BOTH bounds because every
 * engine does: the Gremlin/Elasticsearch builders fail a one-sided range
 * closed (match nothing) and `validateFilterExpression` rejects it.
 *
 * Deliberately catalog-independent so it can decide what a host persists: a
 * row whose aspect is merely absent from a still-loading (or narrowed) catalog
 * is not incomplete, and must never be dropped for that.
 */
export function isFilterExpressionComplete(filter: FilterExpression): boolean {
  if (!filter.property) return false;
  if (VALUELESS_OPERATORS.includes(filter.operator as FilterOperator)) return true;
  if (filter.operator === 'between') return isUsableBetweenBound(filter.min) && isUsableBetweenBound(filter.max);
  if (Array.isArray(filter.values)) return filter.values.length > 0;
  if (Array.isArray(filter.value)) return filter.value.length > 0;
  return isFilledValue(filter.value);
}

/**
 * Whether a filter row carries everything its operator needs to evaluate — the
 * typed+shared validity check `efFilterValid` in filter-row.jsx made per-row, so an
 * incomplete row (no property, or a value-requiring operator with nothing entered)
 * can be marked invalid and block Apply instead of quietly matching everything or
 * nothing. Lane-typed rows (`arc`/`relationship`/`perspective`) are considered valid
 * once they carry any value object — their own value editors own field-level
 * completeness (e.g. `ArcValueEditorMolecule`'s stage/health pickers); this function
 * only guards the scalar value-entry rows `EditableFilterRowMolecule` renders directly.
 */
export function isFilterExpressionValid(filter: FilterExpression, prop: PropertyOption | undefined): boolean {
  if (!prop) return false;
  return isFilterExpressionComplete(filter);
}

/**
 * Whether any leaf — a property row or a lane condition — can be evaluated as
 * written. A property row is judged by {@link isFilterExpressionComplete}. A
 * perspective or relationship condition is incomplete until it names its
 * perspective / relationship: unconfigured (`defaultLaneFilter`), it carries an
 * empty name that every engine fails closed on, matching nothing
 * (`buildPerspectiveFilterClauses` / `buildRelationshipFilterClauses`). Its
 * other fields are optional by contract — empty `assignees` / `relatedTo`
 * means "any", and an arc condition with every axis empty is an existence
 * check — so those never make a lane leaf incomplete.
 */
export function isFilterGroupLeafComplete(leaf: FilterGroupLeaf): boolean {
  if (isPropertyLeaf(leaf)) return isFilterExpressionComplete(leaf);
  // Optional-chained: a stored filter is JSON and may predate the required name.
  if (isPerspectiveLeaf(leaf)) return Boolean(leaf.filter.perspectiveSystemName?.trim());
  if (isRelationshipLeaf(leaf)) return Boolean(leaf.filter.relationshipSystemName?.trim());
  return true;
}

/**
 * The tree with every incomplete leaf ({@link isFilterGroupLeafComplete})
 * removed, at every depth — what a host persists or sends while the author is still
 * mid-way through a row (spec edge case "Incomplete row while authoring — excluded
 * from the predicate and the runtime body"). The row itself stays in the host's
 * authoring tree; only the predicate omits it. Group structure is kept — an empty
 * group evaluates as no constraint in every engine, exactly like an author's fresh
 * "Add group". Returns the input group (same reference) when nothing was removed.
 */
export function pruneIncompleteLeaves(group: FilterGroup): FilterGroup {
  let changed = false;
  const expressions = (group.expressions ?? []).filter((leaf) => {
    const keep = isFilterGroupLeafComplete(leaf);
    if (!keep) changed = true;
    return keep;
  });
  const groups = group.groups?.map((child) => {
    const next = pruneIncompleteLeaves(child);
    if (next !== child) changed = true;
    return next;
  });
  return changed ? { ...group, expressions, groups } : group;
}

/**
 * The row an operator change produces, with its operand moved to the field the
 * NEW operator reads and the fields it does not read cleared.
 *
 * Each operator reads exactly one operand: `between` the `min`/`max` pair,
 * `in`/`not_in`/`contains_all` the `values` array, everything else the scalar
 * `value` (`expectedOperand`, `@center-platform/spheres/filters`). A row left
 * carrying the previous operator's field silently matches nothing — an `in` with
 * a scalar `value` hands every engine `undefined`, which is not an array — so
 * the migration is a correctness rule, not a convenience, and lives here rather
 * than being re-derived by each editor.
 *
 * A scalar selection is carried INTO the array (and the first array entry back
 * out) so switching "is" to "is any of" keeps the author's answer.
 *
 * @param emptyValue what a scalar row falls back to when there is nothing to carry.
 */
export function applyOperatorChange(
  filter: FilterExpression,
  operator: FilterOperator,
  emptyValue: unknown = ''
): FilterExpression {
  const wasBetween = filter.operator === 'between';
  const isBetween = operator === 'between';
  const wasArray = ARRAY_OPERAND_OPERATORS.includes(filter.operator);
  const isArray = ARRAY_OPERAND_OPERATORS.includes(operator);

  if (isBetween === wasBetween && isArray === wasArray) return { ...filter, operator };
  if (isBetween) return { ...filter, operator, value: undefined, values: undefined };
  if (isArray) {
    let carried: unknown[];
    if (Array.isArray(filter.value)) carried = filter.value;
    else if (filter.value === undefined || filter.value === null || filter.value === '') carried = [];
    else carried = [filter.value];
    return { ...filter, operator, values: carried, value: undefined, min: undefined, max: undefined };
  }
  const first = Array.isArray(filter.values) ? filter.values[0] : undefined;
  return {
    ...filter,
    operator,
    value: first ?? emptyValue,
    values: undefined,
    min: undefined,
    max: undefined,
  };
}

/**
 * Resolve the operator options for a given property type. Typed (only members of
 * `FilterOperator` are ever returned) and localized (every label routes through `t`).
 * Unknown/unsupported types safely return an empty list rather than the full
 * type-blind union — a type-blind list is exactly the bug this extraction fixes
 * (e.g. offering `greater_than` on a boolean property).
 */
export function getFilterOperatorOptions(type: PropertyType, t: TranslateFn): FilterOperatorOption[] {
  switch (type) {
    case 'text':
      return [
        { label: t('filters.operators.contains'), value: 'contains' },
        { label: t('filters.operators.notContains'), value: 'not_contains' },
        { label: t('filters.operators.equals'), value: 'equals' },
        { label: t('filters.operators.notEquals'), value: 'not_equals' },
        { label: t('filters.operators.startsWith'), value: 'starts_with' },
        { label: t('filters.operators.endsWith'), value: 'ends_with' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    case 'numeric':
      return [
        { label: t('filters.operators.equals'), value: 'equals' },
        { label: t('filters.operators.notEquals'), value: 'not_equals' },
        { label: t('filters.operators.greaterThan'), value: 'greater_than' },
        { label: t('filters.operators.greaterThanEquals'), value: 'greater_than_equals' },
        { label: t('filters.operators.lessThan'), value: 'less_than' },
        { label: t('filters.operators.lessThanEquals'), value: 'less_than_equals' },
        { label: t('filters.operators.between'), value: 'between' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    // `date` and `datetime` differ only in the value editor's granularity (a
    // calendar day vs. an instant), never in which comparisons are meaningful —
    // `datetime` previously withheld the four relative-date operators `date`
    // offered, which was an omission rather than a decision.
    case 'date':
    case 'datetime':
      return [
        { label: t('filters.operators.on'), value: 'equals' },
        { label: t('filters.operators.before'), value: 'less_than' },
        { label: t('filters.operators.onOrBefore'), value: 'less_than_equals' },
        { label: t('filters.operators.after'), value: 'greater_than' },
        { label: t('filters.operators.onOrAfter'), value: 'greater_than_equals' },
        { label: t('filters.operators.between'), value: 'between' },
        { label: t('filters.operators.withinLastDays'), value: 'within_last_days' },
        { label: t('filters.operators.withinNextDays'), value: 'within_next_days' },
        // Symmetric — its label says "past or future" so it can never be read as
        // a synonym of the two directional windows above it.
        { label: t('filters.operators.dateWithinDays'), value: 'date_within_days' },
        { label: t('filters.operators.last7Days'), value: 'last_7_days' },
        { label: t('filters.operators.last30Days'), value: 'last_30_days' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    case 'enum':
      return [
        { label: t('filters.operators.equals'), value: 'equals' },
        { label: t('filters.operators.notEquals'), value: 'not_equals' },
        { label: t('filters.operators.in'), value: 'in' },
        { label: t('filters.operators.containsAll'), value: 'contains_all' },
        { label: t('filters.operators.notIn'), value: 'not_in' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    case 'boolean':
      // No emptiness pair: a boolean's unset state is already expressible as
      // "is false"/"is true" not matching, and a third "is empty" row would put
      // two controls on one fact.
      return [
        { label: t('filters.operators.isTrue'), value: 'is_true' },
        { label: t('filters.operators.isFalse'), value: 'is_false' },
      ];
    case 'urgency':
      return [
        { label: t('filters.operators.equals'), value: 'equals' },
        { label: t('filters.operators.notEquals'), value: 'not_equals' },
        { label: t('filters.operators.greaterThan'), value: 'greater_than' },
        { label: t('filters.operators.greaterThanEquals'), value: 'greater_than_equals' },
        { label: t('filters.operators.lessThan'), value: 'less_than' },
        { label: t('filters.operators.lessThanEquals'), value: 'less_than_equals' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    case 'multi':
    case 'person':
      // Open-ended multi-value properties (people, tags) are membership tests
      // only — a single "equals" reads oddly against a list, so the same
      // in/not_in/contains_all set `enum` offers for its multi-value operators
      // is offered without the single-value equals/not_equals pair. The
      // emptiness pair answers "nobody is assigned" / "has any tag", which
      // membership alone cannot express.
      return [
        { label: t('filters.operators.in'), value: 'in' },
        { label: t('filters.operators.containsAll'), value: 'contains_all' },
        { label: t('filters.operators.notIn'), value: 'not_in' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    case 'spheretype':
    case 'spherefamily':
      // Sphere Type / Sphere Family are set-membership properties (a sphere is
      // one of a chosen set) — matches `resolveOperatorOptions`'s narrowing for
      // the legacy `type`/`label` property keys.
      return [
        { label: t('filters.operators.in'), value: 'in' },
        { label: t('filters.operators.notIn'), value: 'not_in' },
      ];
    case 'reference':
      // A reference stores the target sphere's identifier, so it is matched by
      // identity or emptiness — never by substring/ordering. Before `reference`
      // became its own PropertyType it was classified as `text` and inherited
      // this list's equality operators; omitting the case here would silently
      // strip filtering from every reference aspect.
      return [
        { label: t('filters.operators.equals'), value: 'equals' },
        { label: t('filters.operators.notEquals'), value: 'not_equals' },
        // Set membership over target spheres — the prototype's "is any of" /
        // "is none of". The operand is the `values` array of target sphere
        // identifiers, which is exactly what the stored property holds.
        { label: t('filters.operators.in'), value: 'in' },
        { label: t('filters.operators.notIn'), value: 'not_in' },
        { label: t('filters.operators.isEmpty'), value: 'is_empty' },
        { label: t('filters.operators.isNotEmpty'), value: 'is_not_empty' },
      ];
    default:
      // Includes 'arc' (Phase 2 suppresses operator chrome for arc rows entirely)
      // and any future additive PropertyType member — safe empty default.
      return [];
  }
}

/**
 * The operator a freshly picked aspect row opens with, by data type — the prototype's
 * `EF_OPERATORS[type][0]` rule (filter-row.jsx:146): "contains" for text, "=" for numbers,
 * "is" for dates and booleans, "is any of" (`in`) for every set-valued type. Before this the
 * catalog minted `in` for everything, so a text aspect opened as `Description in …` — an
 * operator the row's own operator list does not even offer (EAINT-908 host-frame finding).
 *
 * Every value here is one `getFilterOperatorOptions(type)` offers for that type
 * (`defaultOperatorIsAlwaysOffered` pins it), so a row never opens on an operator its
 * chip cannot display. Lane pseudo-types and unknown types fall back to `in`, the lanes'
 * own default.
 */
export function defaultOperatorFor(type: PropertyType | undefined): FilterOperator {
  switch (type) {
    case 'text':
      return 'contains';
    case 'numeric':
    case 'urgency':
    case 'date':
    case 'datetime':
    case 'reference':
      return 'equals';
    case 'boolean':
      return 'is_true';
    case 'enum':
    case 'multi':
    case 'person':
    case 'spheretype':
    case 'spherefamily':
      return 'in';
    default:
      return 'in';
  }
}
